<template>
  <div class="module-subnav-container">
    <!-- Mobile view - icons only -->
    <div class="field is-grouped is-hidden-widescreen mobile-nav">
      <p v-for="(item, index) in visibleMenuItems"
        :key="`mobile-${index}`"
        class="control">
        <router-link v-if="item.route"
          :to="item.route"
          class="button tooltip is-tooltip-bottom is-small"
          :class="{ 'is-disabled': item.disabled }"
          active-class="is-active"
          @click.native="handleItemClick(item, $event)"
          :exact="item.exact !== false"
          :title="item.label"
          :data-tooltip="item.label">
          <span class="icon"
            :class="[item.badgeClass, { 'has-new-content': item.hasNewContent }]"
            :data-badge="item.badge">
            <span class="mso mso-20px">
              {{ item.icon || getDefaultIcon(item.label) }}
            </span>
          </span>
        </router-link>
        <a v-else
          class="button tooltip is-tooltip-bottom is-small"
          :class="{ 'is-disabled': item.disabled }"
          @click="handleItemClick(item, $event)"
          :title="item.label"
          :data-tooltip="item.label">
          <span class="icon"
            :class="[item.badgeClass, { 'has-new-content': item.hasNewContent }]"
            :data-badge="item.badge">
            <span class="mso mso-20px">
              {{ item.icon || getDefaultIcon(item.label) }}
            </span>
          </span>
        </a>
      </p>

      <!-- Mobile More dropdown button -->
      <p v-if="hasMoreItems"
        class="control">
        <a name="mobileMoreMenuButton"
          class="button is-small">
          <span class="icon">
            <span class="mso mso-20px">more_vert</span>
          </span>
        </a>
      </p>
    </div>

    <!-- Desktop view - full text -->
    <ul class="menu-list horizontal is-hidden-mobile is-hidden-tablet-only is-hidden-desktop-only">
      <li v-for="(item, index) in visibleMenuItems"
        :key="index">
        <router-link v-if="item.route"
          :to="item.route"
          active-class="is-active"
          :class="{ 'is-disabled': item.disabled }"
          @click.native.prevent="handleItemClick(item, $event)"
          :exact="item.exact !== false">
          <span class="is-flex is-align-items-center is-justify-content-space-between">
            <span class="is-flex is-align-items-center">
              <span class="icon">
                <span class="mso mso-20px">
                  {{ item.icon || getDefaultIcon(item.label) }}
                </span>
              </span>
              <span :class="[item.badgeClass, { 'has-new-content': item.hasNewContent }]"
                class="ml-1"
                :data-badge="item.badge">{{ item.label }}</span>
            </span>
          </span>
        </router-link>
        <a v-else
          :class="{ 'is-disabled': item.disabled }"
          @click="handleItemClick(item, $event)">
          <span class="is-flex is-align-items-center is-justify-content-space-between">
            <span class="is-flex is-align-items-center">
              <span class="icon">
                <span class="mso mso-20px mr-2">
                  {{ item.icon || getDefaultIcon(item.label) }}
                </span>
              </span>
              <span :class="[item.badgeClass, { 'has-new-content': item.hasNewContent }]"
                :data-badge="item.badge">{{ item.label }}</span>
            </span>
          </span>
        </a>
        <!-- Custom action slot for menu items -->
        <slot name="menu-item-actions"
          :item="item"
          :handle-action="handleCustomAction" />
      </li>

      <!-- Desktop More dropdown button -->
      <li v-if="hasMoreItems"
        class="is-flex">
        <div name="moreMenuButton"
          class="button is-small px-4 is-align-self-center moreMenuButton"
          :class="`has-hero-button-text-${$theme.name}`"
          style="border: none;">
          <span class="icon">
            <span class="mso mso-20px">more_vert</span>
          </span>
        </div>
      </li>
    </ul>

    <!-- More actions dropdown menu -->
    <tippy v-if="hasMoreItems"
      ref="tippyMoreMenu"
      to="moreMenuButton"
      trigger="click"
      arrow
      :interactive="true"
      :theme="`${$theme.name}`"
      :max-width="500">
      <div class="menu more-menu">
        <ul class="menu-list">
          <li v-for="(item, index) in visibleMoreItems"
            :key="index">
            <router-link v-if="item.route"
              :to="item.route"
              active-class="is-active"
              :class="{ 'is-disabled': item.disabled }"
              @click.native="handleMoreItemClick(item)"
              :exact="item.exact !== false">
              <span class="is-flex is-align-items-center">
                <span class="icon pr-3">
                  <span class="mso mso-20px">{{ item.icon || getDefaultIcon(item.label) }}</span>
                </span>
                <span :class="[item.badgeClass, { 'has-new-content': item.hasNewContent }]"
                  :data-badge="item.badge">{{ item.label }}</span>
              </span>
            </router-link>
            <a v-else
              class="is-flex is-align-items-center"
              :class="{ 'is-disabled': item.disabled }"
              @click="handleMoreItemClick(item)">
              <span class="icon pr-3">
                <span class="mso mso-20px">{{ item.icon || getDefaultIcon(item.label) }}</span>
              </span>
              <span :class="[item.badgeClass, { 'has-new-content': item.hasNewContent }]"
                :data-badge="item.badge">{{ item.label }}</span>
            </a>
          </li>
        </ul>
      </div>
    </tippy>

    <!-- Mobile More actions dropdown menu -->
    <tippy v-if="hasMoreItems"
      ref="tippyMobileMoreMenu"
      to="mobileMoreMenuButton"
      trigger="click"
      arrow
      :interactive="true"
      :max-width="500">
      <div class="menu more-menu">
        <ul class="menu-list">
          <li v-for="(item, index) in visibleMoreItems"
            :key="index">
            <router-link v-if="item.route"
              :to="item.route"
              active-class="is-active"
              :class="{ 'is-disabled': item.disabled }"
              @click.native="handleMobileMoreItemClick(item)"
              :exact="item.exact !== false">
              <span class="is-flex is-align-items-center">
                <span v-if="item.icon"
                  class="icon pr-3">
                  <span class="mso mso-20px mr-2">{{ item.icon }}</span>
                </span>
                <span :class="[item.badgeClass, { 'has-new-content': item.hasNewContent }]"
                  :data-badge="item.badge">{{ item.label }}</span>
              </span>
            </router-link>
            <a v-else
              class="is-flex is-align-items-center"
              :class="{ 'is-disabled': item.disabled }"
              @click="handleMobileMoreItemClick(item)">
              <span v-if="item.icon"
                class="icon pr-3">
                <span class="mso mso-20px mr-2">{{ item.icon }}</span>
              </span>
              <span :class="[item.badgeClass, { 'has-new-content': item.hasNewContent }]"
                :data-badge="item.badge">{{ item.label }}</span>
            </a>
          </li>
        </ul>
      </div>
    </tippy>
  </div>
</template>

<script>
export default {
  name: 'ModuleSubNav',
  props: {
    menuItems: {
      type: Array,
      default: () => [],
      validator: (items) => {
        return items.every((item) => typeof item === 'object' && typeof item.label === 'string')
      }
    },
    menuItemsMore: {
      type: Array,
      default: () => [],
      validator: (items) => {
        return items.every((item) => typeof item === 'object' && typeof item.label === 'string')
      }
    },
    showOnlyVisible: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    visibleMenuItems() {
      if (this.showOnlyVisible) {
        return this.menuItems.filter((item) => item.visible !== false)
      }
      return this.menuItems
    },
    visibleMoreItems() {
      if (this.showOnlyVisible) {
        return this.menuItemsMore.filter((item) => item.visible !== false)
      }
      return this.menuItemsMore
    },
    hasMoreItems() {
      return this.visibleMoreItems.length > 0
    }
  },
  methods: {
    handleItemClick(item, event) {
      if (item.disabled) {
        event.preventDefault()
        return
      } else {
        this.$emit('item-click', item)
      }
    },
    handleMoreItemClick(item) {
      this.$emit('item-click', item)
      // Close the dropdown after clicking an item
      if (this.$refs.tippyMoreMenu && this.$refs.tippyMoreMenu.tip) {
        this.$refs.tippyMoreMenu.tip.hide()
      }
    },
    handleMobileMoreItemClick(item) {
      this.$emit('item-click', item)
      // Close the dropdown after clicking an item
      if (this.$refs.tippyMobileMoreMenu && this.$refs.tippyMobileMoreMenu.tip) {
        this.$refs.tippyMobileMoreMenu.tip.hide()
      }
    },
    handleCustomAction(item, actionType, ...args) {
      this.$emit('custom-action', { item, actionType, args })
    },
    getDefaultIcon(label) {
      // Return default icons based on label
      const iconMap = {
        'Header': 'account_circle',
        'Quoting': 'edit_document',
        'Parts': 'car_tag',
        'Invoice': 'receipt_long',
        'Excess': 'receipt',
        'Assessments': 'verified_user',
        'Images': 'imagesmode',
        'Summary': 'summarize',
        'PNET': 'data_table',
        'AudaBridge': 'data_table',
        'AudaNet': 'data_table',
        'ORM': 'message',
        'RepX': 'analytics',
        'ARNIE': 'smart_toy',
        'EstImage': 'photo_library',
        'Rates & Markups': 'percent',
        'Remarks': 'sticky_note_2',
        'Annotations': 'edit',
        'Comms': 'mark_as_unread',
        'Docs': 'docs',
        'Other Labour': 'work'
      }

      // Extract the base label (remove any additional text after spaces or slashes)
      const baseLabel = label.split(/[\/\s]/)[0]
      if (iconMap[baseLabel]) {
        return iconMap[baseLabel]
      }
      if (iconMap[label]) {
        return iconMap[label]
      }
      const foundKey = Object.keys(iconMap).find(key => label.startsWith(key))
      if (foundKey) {
        return iconMap[foundKey]
      }
      return 'more_horiz'
    }
  }
}
</script>

<style lang="scss" scoped>
@use '@/assets/style/override-colours' as oc;

.module-subnav-container {
  width: 100%;
  position: relative;
  padding-top: 0.5rem;
  border-top: solid 1px var(--divider-background-color);
}

.mobile-nav {
  padding: 0.5rem 0rem;
  // overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-bottom: 0;

  .button {
    min-width: auto;

    // &.is-active {
    //   background-color: hsl(217, 71%, 53%);
    //   color: white;
    // }
  }
}

.menu-list.horizontal {
  display: flex;
  flex-wrap: nowrap;
  gap: 1rem;
  list-style: none;
  margin: 0;
  padding: 0;
  white-space: nowrap;
  min-width: max-content;
  // border-bottom: solid 1px #dee2e6;

  li {
    margin: 0;
    flex-shrink: 0;
    position: relative;

    a {
      display: inline-block;
      padding: 0.2rem 0;
      text-decoration: none;
      // color: #495057;
      background-color: transparent;
      border: none;
      font-weight: 500;
      // font-size: 0.9rem;
      //border-bottom: 2px solid transparent;
      position: relative;
      font-size: clamp(0.1rem, 1vw, 0.9rem);
      width: auto;

      &:hover {
        background-color: transparent;
        color: #3291EF;
      }

      &.is-active {
        background-color: transparent;
        color: #3291EF;
        font-weight: 600;
        border-bottom: 2px solid #3291EF;
      }

      &.is-disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }

  [class*='has-badge-'] {
    position: relative;
    z-index: 25;

    &::after {
      content: attr(data-badge);
      position: absolute;
      top: -0.7em;
      right: -1.2em;
      display: inline-block;
      min-width: 1em;
      padding: 0.1em 0.3em;
      font-size: 0.75em;
      font-weight: bold;
      line-height: 1;
      text-align: center;
      white-space: nowrap;
      vertical-align: baseline;
      z-index: 9999 !important;
      pointer-events: none;
      transform: translateZ(0);
    }

    [class*='has-badge-']::after {
      min-height: 1.15em;
      font-size: 0.85em;
    }

    // New-content dot, on the top-right shoulder of the count badge above.
    // It has to be anchored and stacked against THAT badge, not the global one
    // in bulma-badge.sass: this rule repositions the badge to top/right -0.7em
    // /-1.2em and lifts it to z-index 9999, so a dot placed for the global
    // badge ends up buried underneath it and in the wrong spot.
    &.has-new-content::before {
      content: '';
      position: absolute;
      // 1px clear of the badge on both axes. Size is a whole number of pixels,
      // not an em: 0.4em computed to 4.34px, which the browser snapped to a
      // different width and height and rendered as an oval, not a circle.
      top: calc(-0.525em - 1px);
      right: calc(-0.9em - 1px);
      width: 5px;
      height: 5px;
      border-radius: 50%;
      background: oc.$danger;
      transform: translate(50%, -50%);
      z-index: 10000;
      pointer-events: none;
    }
  }
}

// Dropdown menu styles
.menu.more-menu {
  min-width: 12rem !important;

  .menu-list {
    padding: 0;
    margin: 0;
    border-inline-start: none;
    // color: #495057;

    a {
      padding: 0.5rem 0.75rem;
      border-radius: 4px;
      text-decoration: none;
      color: inherit;

      // &:hover {
      //   background-color: hsl(0, 0%, 96%);
      // }

      &.is-active {
        border-bottom: 0 !important;
        // background-color: hsl(217, 71%, 53%);
        // color: white;
      }
    }
  }
}

.is-disabled {
  pointer-events: none;
  opacity: 0.5;
  cursor: not-allowed;
}

</style>